/**
 * @fileoverview Provider wrapper for next-themes theme management.
 *
 * This module provides a thin wrapper around next-themes ThemeProvider
 * to enable dark/light theme switching with system preference support.
 *
 * @module components/providers/theme-provider
 */

"use client";

import { ThemeProvider as NextThemesProvider } from "next-themes";
import type * as React from "react";

/**
 * Theme provider wrapper for next-themes.
 *
 * @description
 * Wraps the next-themes ThemeProvider to enable:
 * - Dark/light theme switching
 * - System theme preference detection
 * - Persistent theme selection
 * - Server-side rendering support
 *
 * @param children - React children to be wrapped with theme provider
 * @param props - Additional props passed to next-themes ThemeProvider
 * @returns ThemeProvider component
 *
 * @example
 * ```tsx
 * <ThemeProvider
 *   attribute="class"
 *   defaultTheme="light"
 *   enableSystem
 *   disableTransitionOnChange
 * >
 *   <App />
 * </ThemeProvider>
 * ```
 */
export function ThemeProvider({
  children,
  ...props
}: React.ComponentProps<typeof NextThemesProvider>) {
  return <NextThemesProvider {...props}>{children}</NextThemesProvider>;
}
